/**
 * Branch Price Service
 * Şube bazlı fiyat yönetimi için API servisleri
 */

import { accountingApi } from '../../../../config/apiClient';

// ============== TYPES ==============

export interface BranchPriceItem {
  id: string;
  companyId: string;
  inventoryItemId: string;
  branchId: string;
  branchName?: string;
  salePrice: number;
  saleCurrency: string;
  listPrice?: number;
  listCurrency?: string;
  packagePrice?: number;
  /** Şubeye özel alış fiyatı; boşsa ürün kartındaki alış fiyatı geçerlidir. */
  purchasePrice?: number | null;
  purchaseCurrency?: string | null;
  itemPurchasePrice?: number | null;
  itemPurchaseCurrency?: string | null;
  /** Alış faturasının açtığı satır: şube satış fiyatı tanımlı değil, ürün kartındaki geçerli. */
  isPurchaseOnly?: boolean;
  itemSalePrice?: number | null;
  itemSaleCurrency?: string | null;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  isActive: boolean;
  // ✅ Ürün bilgileri (API'dan dönen)
  stockCode?: string;
  productName?: string;
  categoryId?: string;
  categoryName?: string;
  unit?: string;
  barcode?: string;
  // ✅ YENİ: Ürünün şube fiyatlandırma kullanım durumu
  useBranchPricing?: boolean;
}

export interface BranchPriceWithProduct extends BranchPriceItem {
  stockCode: string;
  productName: string;
  categoryName?: string;
  brandName?: string;
  unit?: string;
  barcode?: string;
}

// ============== SERVICE ==============

export class BranchPriceService {
  /**
   * Şubeye ait tüm fiyatları getir
   * @param companyId Şirket ID
   * @param branchId Şube ID
   * @returns BranchPriceItem[]
   */
  static async getBranchPrices(companyId: string, branchId: string): Promise<BranchPriceItem[]> {
    try {
      console.log(`📊 [BRANCH PRICE SERVICE] Şube fiyatları getiriliyor: Branch=${branchId}, Company=${companyId}`);
      
      const response = await accountingApi.get(
        `/api/InventoryBranchPrice/branch/${branchId}`
      );
      
      console.log(`✅ [BRANCH PRICE SERVICE] ${response.data.length} fiyat bulundu`);
      return response.data;
    } catch (error: any) {
      // 404 = kayıt yok - boş array dön
      if (error.response?.status === 404) {
        console.log(`ℹ️ [BRANCH PRICE SERVICE] Bu şube için fiyat bulunamadı`);
        return [];
      }
      console.error(`❌ [BRANCH PRICE SERVICE] Hata:`, error);
      throw error;
    }
  }

  /**
   * Yeni şube fiyatı oluştur
   */
  static async createBranchPrice(dto: {
    companyId: string;
    inventoryItemId: string;
    branchId: string;
    branchName?: string;
    salePrice: number;
    saleCurrency?: string;
    listPrice?: number;
    listCurrency?: string;
    packagePrice?: number;
    purchasePrice?: number;
    purchaseCurrency?: string;
    createdBy?: string;
  }): Promise<BranchPriceItem> {
    console.log(`📝 [BRANCH PRICE SERVICE] Yeni fiyat oluşturuluyor:`, dto);
    const response = await accountingApi.post(`/api/InventoryBranchPrice`, dto);
    console.log(`✅ [BRANCH PRICE SERVICE] Fiyat oluşturuldu:`, response.data);
    return response.data;
  }

  /**
   * Şube fiyatını güncelle
   * ⚠️ CRITICAL: Backend PascalCase bekliyor! (CRITICAL_API_DEVELOPMENT_RULES.md)
   */
  static async updateBranchPrice(
    companyId: string, 
    id: string, 
    dto: {
      salePrice: number;
      saleCurrency?: string;
      listPrice?: number;
      listCurrency?: string;
      packagePrice?: number;
      /** Gönderilmezse sunucudaki alış fiyatı korunur. */
      purchasePrice?: number;
      purchaseCurrency?: string;
      /** true: şube alış fiyatını kaldır, ürün kartındaki geçerli olsun. */
      clearPurchasePrice?: boolean;
      /** false: satır artık şube satış fiyatı taşır. Gönderilmezse sunucu karar verir. */
      isPurchaseOnly?: boolean;
      // ✅ YENİ: Fiyat değişiklik log için kullanıcı bilgileri
      userId?: string;
      userName?: string;
      branchId?: string;
      branchName?: string;
    }
  ): Promise<BranchPriceItem> {
    console.log(`📝 [BRANCH PRICE SERVICE] Fiyat güncelleniyor: ${id}, userId: ${dto.userId}`, dto);
    
    // Backend PascalCase bekliyor - dönüştür
    const payload = {
      SalePrice: dto.salePrice,
      SaleCurrency: dto.saleCurrency || 'TRY',
      ListPrice: dto.listPrice || null,
      ListCurrency: dto.listCurrency || null,
      PackagePrice: dto.packagePrice || null,
      PurchasePrice: dto.purchasePrice ?? null,
      PurchaseCurrency: dto.purchaseCurrency || null,
      ClearPurchasePrice: dto.clearPurchasePrice === true,
      IsPurchaseOnly: dto.isPurchaseOnly ?? null,
      UserId: dto.userId || null,
      UserName: dto.userName || null,
      BranchId: dto.branchId || null,
      BranchName: dto.branchName || null
    };
    
    const response = await accountingApi.post(
      `/api/InventoryBranchPrice/update/${id}`, 
      payload
    );
    console.log(`✅ [BRANCH PRICE SERVICE] Fiyat güncellendi:`, response.data);
    return response.data;
  }

  /**
   * Şube fiyatını sil
   */
  static async deleteBranchPrice(companyId: string, id: string): Promise<void> {
    console.log(`🗑️ [BRANCH PRICE SERVICE] Fiyat siliniyor: ${id}`);
    await accountingApi.post(`/api/InventoryBranchPrice/delete/${id}`);
    console.log(`✅ [BRANCH PRICE SERVICE] Fiyat silindi`);
  }
}

export default BranchPriceService;
